import {
  useGetConfiguration,
  useUpdateConfiguration,
} from '@/hooks/useConfiguration';
import { useMy } from '@/hooks/useMy';
import type { ScanCadenceBase, ScanSchedule } from '@/types';

/**
 * Reads the per-tenant base scan cadence. The cadence now lives in a
 * Praetorian-only `scan-cadence` Configuration (`value` = `ScanCadenceBase`);
 * the backend stamps `anchorDate` on save. For un-migrated tenants we fall back
 * to the legacy `cadence` object nested inside the `schedule` setting document.
 * Writes go through `updateScanCadenceAsync`, which PUTs the `scan-cadence`
 * Configuration.
 */

// Name of the Praetorian-only Configuration carrying the base scan cadence.
const SCAN_CADENCE_CONFIG_NAME = 'scan-cadence';

// Default to 'daily' on an unset tenant: the backend's unset behavior is
// daily-like (fail-open), so a no-op Save preserves today's behavior.
const DEFAULT_CADENCE: ScanCadenceBase = {
  frequency: 'daily',
  dayOfWeek: 0,
  dayOfMonth: 1,
  time: '00:00',
};

export function useScanCadence({ email }: { email?: string } = {}) {
  const {
    data: configurations,
    isPending,
    status,
    refetch,
  } = useGetConfiguration({ email });

  // fallback to legacy schedule.cadence until the scan-cadence backfill migration completes
  const { data: settings } = useMy(
    { resource: 'setting' },
    {
      email,
      staleTime: 30000, // 30 seconds
      gcTime: 300000, // 5 minutes
    }
  );

  const configCadence = configurations?.find(
    c => c.name === SCAN_CADENCE_CONFIG_NAME
  )?.value as ScanCadenceBase | undefined;

  const schedule = settings?.find(s => s.name === 'schedule')?.value as
    | ScanSchedule
    | undefined;
  const legacyCadence = schedule?.cadence;

  // Prefer the Configuration when present; either source counts as "set".
  const isSet = configCadence !== undefined || legacyCadence !== undefined;
  const currentCadence: ScanCadenceBase =
    configCadence ?? legacyCadence ?? DEFAULT_CADENCE;

  const { mutateAsync: updateConfiguration } = useUpdateConfiguration({
    email,
  });

  const updateScanCadenceAsync = (cadence: ScanCadenceBase) =>
    updateConfiguration({ name: SCAN_CADENCE_CONFIG_NAME, value: cadence });

  return {
    currentCadence,
    isSet,
    isLoadingCadence: isPending,
    status,
    refetch,
    updateScanCadenceAsync,
  };
}
